import React, { useCallback, useEffect, useMemo, useState } from 'react';
import T from 'i18n';
import { Alert, Stack, TextField, Typography } from '@mui/material';
import { Modal } from 'features/common/MaterialBasedComponents';
import api from 'common/api';
import { useToasts } from 'react-toast-notifications';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';

type CreatePlaceholdersModalProps = {
  open: boolean;
  folderId?: string | null;
  onClose: () => void;
  onCreated: () => void;
};

type ValidationErrors = {
  count?: string;
  startNumber?: string;
  insertAtIndex?: string;
};

const extractErrorMessage = (error: any) => {
  const fallback =
    T.translate('case.placeholdersCreateError') || 'Could not create placeholders. Please try again.';

  if (!error) return fallback;
  if (typeof error === 'string' && error.trim()) return error;

  if (typeof error?.body === 'string' && error.body.trim()) return error.body;

  if (error?.body && typeof error.body === 'object') {
    if (typeof error.body.message === 'string' && error.body.message.trim()) {
      return error.body.message;
    }
    if (typeof error.body.title === 'string' && error.body.title.trim()) {
      return error.body.title;
    }
    if (typeof error.body.body === 'string' && error.body.body.trim()) {
      return error.body.body;
    }
    if (typeof error.body.statusText === 'string' && error.body.statusText.trim()) {
      return error.body.statusText;
    }
  }

  if (typeof error?.statusText === 'string' && error.statusText.trim()) {
    return error.statusText;
  }

  return fallback;
};

const getValidationErrors = (
  count: string,
  startNumber: string,
  insertAtIndex: string,
): ValidationErrors => {
  const errors: ValidationErrors = {};

  const countValue = count.trim();
  if (!countValue) {
    errors.count =
      T.translate('case.placeholderValidationCountRequired') || 'Count is required.';
  } else if (!/^\d+$/.test(countValue)) {
    errors.count =
      T.translate('case.placeholderValidationCountRange') || 'Count must be between 1 and 100.';
  } else {
    const parsedCount = parseInt(countValue, 10);
    if (!Number.isFinite(parsedCount) || parsedCount < 1 || parsedCount > 100) {
      errors.count =
        T.translate('case.placeholderValidationCountRange') || 'Count must be between 1 and 100.';
    }
  }

  const startNumberValue = startNumber.trim();
  if (startNumberValue) {
    if (!/^\d+$/.test(startNumberValue) || parseInt(startNumberValue, 10) < 1) {
      errors.startNumber =
        T.translate('case.placeholderValidationStartNumber') || 'Start number must be 1 or greater.';
    }
  }

  const insertAtIndexValue = insertAtIndex.trim();
  if (insertAtIndexValue) {
    if (!/^\d+$/.test(insertAtIndexValue) || parseInt(insertAtIndexValue, 10) < 0) {
      errors.insertAtIndex =
        T.translate('case.placeholderValidationInsertAtIndex') ||
        'Insert at index must be 0 or greater.';
    }
  }

  return errors;
};

const isIntegerInput = (value: string) => value === '' || /^\d+$/.test(value);

const preventNonIntegerKeys = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (['e', 'E', '+', '-', '.'].includes(event.key)) {
    event.preventDefault();
  }
};

const CreatePlaceholdersModal = ({
  open,
  folderId,
  onClose,
  onCreated,
}: CreatePlaceholdersModalProps) => {
  const caseId = useSelector(selectCurrentCaseId);
  const { addToast } = useToasts();

  const [count, setCount] = useState('1');
  const [namePrefix, setNamePrefix] = useState('Placeholder');
  const [startNumber, setStartNumber] = useState('1');
  const [insertAtIndex, setInsertAtIndex] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const resetState = useCallback(() => {
    setCount('1');
    setNamePrefix('Placeholder');
    setStartNumber('1');
    setInsertAtIndex('');
    setTouched({});
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError('');
  }, []);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const validationErrors = useMemo(
    () => getValidationErrors(count, startNumber, insertAtIndex),
    [count, insertAtIndex, startNumber],
  );
  const isFormValid = Object.keys(validationErrors).length === 0;

  const handleSubmit = useCallback(async () => {
    if (submitting) return;

    setSubmitted(true);
    setSubmitError('');
    if (!isFormValid || !folderId || !caseId) return;

    const payload: Record<string, any> = {
      count: parseInt(count.trim(), 10),
    };

    const trimmedPrefix = namePrefix.trim();
    if (trimmedPrefix) {
      payload.namePrefix = trimmedPrefix;
    }

    const trimmedStartNumber = startNumber.trim();
    if (trimmedStartNumber) {
      payload.startNumber = parseInt(trimmedStartNumber, 10);
    }

    const trimmedInsertAtIndex = insertAtIndex.trim();
    if (trimmedInsertAtIndex) {
      payload.insertAtIndex = parseInt(trimmedInsertAtIndex, 10);
    }

    setSubmitting(true);
    try {
      const response: any = await api.post(
        `/cases/${caseId}/folders/${folderId}/placeholders`,
        payload,
      );

      if (response?.error) {
        setSubmitError(extractErrorMessage(response.error));
        return;
      }

      const createdCountFromResponse = Number(response?.count);
      const createdCount =
        Number.isFinite(createdCountFromResponse) && createdCountFromResponse > 0
          ? createdCountFromResponse
          : Array.isArray(response?.results)
            ? response.results.length
            : payload.count;

      onClose();
      onCreated();
      addToast(
        T.translate('case.placeholdersCreateSuccess', { count: createdCount }) ||
          `Created ${createdCount} placeholder(s).`,
        {
          appearance: 'success',
          autoDismiss: true,
        },
      );
    } catch (error: any) {
      setSubmitError(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [
    addToast,
    caseId,
    count,
    folderId,
    insertAtIndex,
    isFormValid,
    namePrefix,
    onClose,
    onCreated,
    startNumber,
    submitting,
  ]);

  return (
    <Modal
      show={open}
      handleClose={onClose}
      handleSave={handleSubmit}
      disableSave={!isFormValid || submitting}
      loadingBtn={submitting}
      saveTitle={T.translate('case.placeholderCreate') || 'Create'}
      title={T.translate('case.createPlaceholdersTitle') || 'Create placeholders'}
      closeTitle={T.translate('generic.close')}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {T.translate('case.createPlaceholdersDescription') ||
            'Insert placeholder PDF files to reserve positions for future evidence.'}
        </Typography>
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        <TextField
          label={T.translate('case.placeholderCount') || 'Count'}
          type="number"
          fullWidth
          required
          value={count}
          onChange={(event) => {
            const nextValue = event.target.value;
            if (!isIntegerInput(nextValue)) return;
            setCount(nextValue);
            setSubmitError('');
          }}
          onKeyDown={preventNonIntegerKeys}
          onBlur={() => setTouched((previous) => ({ ...previous, count: true }))}
          inputProps={{ min: 1, max: 100, step: 1 }}
          error={Boolean((touched.count || submitted) && validationErrors.count)}
          helperText={(touched.count || submitted) && validationErrors.count ? validationErrors.count : ' '}
        />
        <TextField
          label={T.translate('case.placeholderNamePrefix') || 'Name prefix'}
          fullWidth
          value={namePrefix}
          onChange={(event) => {
            setNamePrefix(event.target.value);
            setSubmitError('');
          }}
        />
        <TextField
          label={T.translate('case.placeholderStartNumber') || 'Start number'}
          type="number"
          fullWidth
          value={startNumber}
          onChange={(event) => {
            const nextValue = event.target.value;
            if (!isIntegerInput(nextValue)) return;
            setStartNumber(nextValue);
            setSubmitError('');
          }}
          onKeyDown={preventNonIntegerKeys}
          onBlur={() => setTouched((previous) => ({ ...previous, startNumber: true }))}
          inputProps={{ min: 1, step: 1 }}
          error={Boolean((touched.startNumber || submitted) && validationErrors.startNumber)}
          helperText={
            (touched.startNumber || submitted) && validationErrors.startNumber
              ? validationErrors.startNumber
              : ' '
          }
        />
        <TextField
          label={T.translate('case.placeholderInsertAtIndex') || 'Insert at index'}
          type="number"
          fullWidth
          value={insertAtIndex}
          onChange={(event) => {
            const nextValue = event.target.value;
            if (!isIntegerInput(nextValue)) return;
            setInsertAtIndex(nextValue);
            setSubmitError('');
          }}
          onKeyDown={preventNonIntegerKeys}
          onBlur={() => setTouched((previous) => ({ ...previous, insertAtIndex: true }))}
          inputProps={{ min: 0, step: 1 }}
          error={Boolean((touched.insertAtIndex || submitted) && validationErrors.insertAtIndex)}
          helperText={
            (touched.insertAtIndex || submitted) && validationErrors.insertAtIndex
              ? validationErrors.insertAtIndex
              : ' '
          }
        />
      </Stack>
    </Modal>
  );
};

export default CreatePlaceholdersModal;
